import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button, Tabs, TabsList, TabsContent } from "@nexus/ui";
import { Upload } from "lucide-react";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePermissions, useIamPermission } from "@/features/auth/hooks/usePermissions";
import type { MarketplaceSearch, MarketplaceTab } from "@/routes/_authenticated/marketplace.index";
import { buildInitialOpportunitiesLinkSearch } from "@/features/opportunities/constants/opportunity-filters";
import { ordersQueryOptions } from "@/lib/queries";
import { VendorsTab } from "../components/vendors/VendorsTab";
import { OrdersTab } from "../components/OrdersTab";
import { HistoryTab } from "../components/HistoryTab";

interface MarketplacePageProps {
  search: MarketplaceSearch & { tab: MarketplaceTab };
  onSearchChange: (patch: Partial<MarketplaceSearch>) => void;
}

export function MarketplacePage({ search, onSearchChange }: MarketplacePageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = usePermissions();
  const canImport =
    useIamPermission(perms.MARKETPLACE_ADMIN_PARSE_ORDER_IMPORT) ||
    useIamPermission(perms.MARKETPLACE_ADMIN_VIEW_PAGE);
  const activeTenantId = useAuth((s) => s.activeTenantId);

  const activeOrders = useQuery(
    ordersQueryOptions(activeTenantId, { fulfillmentGroup: "active", limit: 1 }),
  );
  const historyOrders = useQuery(
    ordersQueryOptions(activeTenantId, { fulfillmentGroup: "history", limit: 1 }),
  );
  const archivedOrders = useQuery(
    ordersQueryOptions(activeTenantId, { fulfillmentGroup: "archive", limit: 1 }),
  );
  const inProductionCount = activeOrders.data?.total ?? 0;
  const historyCount = historyOrders.data?.total ?? 0;
  const archiveCount = archivedOrders.data?.total ?? 0;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 sm:p-6">
      <header className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("marketplace.title")}
          </h1>
          <p className="max-w-prose text-pretty text-sm text-muted-foreground">
            {t("marketplace.subtitle")}
          </p>
        </div>
        {canImport ? (
          <Button variant="outline" className="shrink-0" asChild>
            <Link to="/marketplace/import">
              <Upload className="mr-2 h-4 w-4" />
              {t("marketplace.admin.import.navAction")}
            </Link>
          </Button>
        ) : null}
      </header>

      <Tabs
        value={search.tab}
        onValueChange={(value) => onSearchChange({ tab: value as MarketplaceTab })}
      >
        <TabsList
          variant="line"
          options={[
            { value: "vendors", label: t("marketplace.tabs.vendors", "Partners") },
            {
              value: "in-production",
              label: t("marketplace.tabs.inProduction"),
              badge: inProductionCount > 0 ? inProductionCount : undefined,
            },
            {
              value: "history",
              label: t("marketplace.tabs.history"),
              badge: historyCount > 0 ? historyCount : undefined,
            },
            {
              value: "archive",
              label: t("marketplace.tabs.archive", "Archive"),
              badge: archiveCount > 0 ? archiveCount : undefined,
            },
          ]}
        />
        <TabsContent value="vendors" className="mt-6">
          {activeTenantId && (
            <VendorsTab
              tenantId={activeTenantId}
              search={search}
              onSearchChange={onSearchChange}
              onSendScripts={() => {
                void navigate({
                  to: "/opportunities",
                  search: buildInitialOpportunitiesLinkSearch({ tab: "active" }),
                });
              }}
            />
          )}
        </TabsContent>
        <TabsContent value="in-production" className="mt-6">
          {activeTenantId && (
            <OrdersTab
              tenantId={activeTenantId}
              variant="in-production"
              highlightOrderId={search.order}
              highlightScriptId={search.script}
            />
          )}
        </TabsContent>
        <TabsContent value="history" className="mt-6">
          {activeTenantId && (
            <HistoryTab
              tenantId={activeTenantId}
              highlightOrderId={search.order}
              highlightScriptId={search.script}
            />
          )}
        </TabsContent>
        <TabsContent value="archive" className="mt-6">
          {activeTenantId && (
            <OrdersTab
              tenantId={activeTenantId}
              variant="archive"
              highlightOrderId={search.order}
              highlightScriptId={search.script}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
